import { useMemo } from 'react';
import {
  streamChat,
  regenerateChat,
  getChatHistory,
  getChatPresets,
  clearChatConversation,
  runChatScenario,
  postChatTelemetry,
  postChatFeedback,
} from '../services/loanChatApi';
import usePiperChat from './usePiperChat';

/**
 * Loan-scoped Piper chat. Thin wrapper over usePiperChat with a loan transport.
 */
export default function useLoanChat({
  loanId,
  isOpen,
  quickActionsEnabled = true,
  quickActionsExpanded = false,
  scenarioEnabled = true,
}) {
  const transport = useMemo(() => {
    if (!loanId) return null;

    return {
      scopeKey: String(loanId),
      streamChat: ({ message, intent, token, signal, onContent, onStatus, onMessageId }) =>
        streamChat({ loanId, message, intent, token, signal, onContent, onStatus, onMessageId }),
      regenerate: ({ token, signal, onContent, onStatus, onMessageId }) =>
        regenerateChat({ loanId, token, signal, onContent, onStatus, onMessageId }),
      getHistory: (token) => getChatHistory(loanId, token),
      getPresets: (token) => getChatPresets(loanId, token),
      clear: (token) => clearChatConversation(loanId, token),
      telemetry: ({ eventType, intent, presetKey, conversationKey, metadata, token }) =>
        postChatTelemetry({
          loanId,
          eventType,
          intent,
          presetKey,
          conversationKey,
          metadata,
          token,
        }),
      feedback: ({ conversationKey, messageId, rating, token }) =>
        postChatFeedback({ loanId, conversationKey, messageId, rating, token }),
      enrichMessage: async (trimmed, intent, presetKey, token) => {
        if (!scenarioEnabled || intent !== 'scenario') return trimmed;

        let scenarioType = '';
        let scenarioValue;
        if (presetKey === 'scenario_more_down') {
          scenarioType = 'more_down_payment';
          scenarioValue = 5;
        } else if (presetKey === 'scenario_payoff_auto') {
          scenarioType = 'payoff_auto_loan';
        } else {
          return trimmed;
        }

        try {
          const scenario = await runChatScenario({ loanId, scenarioType, value: scenarioValue, token });
          const assumptionsText = (scenario.assumptions || []).map((x) => `- ${x}`).join('\n');
          return `${trimmed}

Use these deterministic scenario results as source-of-truth values:
- Baseline LoanAmount: ${scenario.baseline?.loanAmount ?? 'n/a'}
- Baseline LTV: ${scenario.baseline?.ltvRatio ?? 'n/a'}
- Baseline DTI: ${scenario.baseline?.dtiRatio ?? 'n/a'}
- Baseline TotalPayment: ${scenario.baseline?.totalMonthlyPayment ?? 'n/a'}
- Projected LoanAmount: ${scenario.projected?.loanAmount ?? 'n/a'}
- Projected LTV: ${scenario.projected?.ltvRatio ?? 'n/a'}
- Projected DTI: ${scenario.projected?.dtiRatio ?? 'n/a'}
- Projected TotalPayment: ${scenario.projected?.totalMonthlyPayment ?? 'n/a'}
${assumptionsText ? `Assumptions:\n${assumptionsText}` : ''}`;
        } catch (scenarioErr) {
          console.error('Failed to run scenario model:', scenarioErr);
          return trimmed;
        }
      },
    };
  }, [loanId, scenarioEnabled]);

  return usePiperChat({
    transport,
    isOpen: Boolean(isOpen && loanId),
    quickActionsEnabled,
    quickActionsExpanded,
  });
}
